// =========================================================
// Version-poll decision logic and force-reload side effects
// =========================================================
//
// Split out from `useVersionPoll` so the branching is a pure function
// that can be unit-tested without a DOM, timers, or fetch. The hook
// owns the polling; this module owns "given the latest payload and
// what we already know, what should happen".
//
// Browser APIs (sessionStorage, caches, indexedDB) are only touched
// from within the functions, never at module scope, so this stays
// safe to import from a `'use client'` module that still prerenders on
// the server.

export interface VersionPayload {
  // Always non-empty here — empty/absent releases are filtered by the
  // caller before a decision is made.
  release: string;
  forceUiReload: boolean;
}

export type VersionAction = { kind: 'none' } | { kind: 'banner' } | { kind: 'force' };

// Pure decision, and the whole of it: compare what the cluster serves
// against what this bundle was built from.
//
// The comparison holds no state, which is what makes a forced reload
// impossible to miss. Every poll re-evaluates against the running
// bundle's own identity rather than against a remembered observation,
// so a reload that lands back on a stale replica simply tries again on
// the next poll. It settles by succeeding — once the tab runs the
// release the cluster serves, the two agree and nothing further
// happens. A rolling deploy can therefore reload a tab more than once,
// which is the accepted cost of never leaving a client behind.
//
// `ownRelease` is empty in builds made without a release build-arg. A
// client that cannot identify itself has nothing meaningful to compare,
// so it never forces.
export function decideVersionAction(payload: VersionPayload, ownRelease: string): VersionAction {
  if (ownRelease === '' || payload.release === ownRelease) {
    return { kind: 'none' };
  }

  // A different release is being served — a deploy, or a rollback
  // (string inequality catches both directions; we intentionally do not
  // try to order releases).
  return payload.forceUiReload ? { kind: 'force' } : { kind: 'banner' };
}

// Thin seam around the actual navigation. jsdom marks
// `window.location.reload` non-configurable, so it cannot be spied
// directly; routing the call through this wrapper lets tests assert (and
// stub) the reload without touching `window.location`.
export function triggerPageReload(): void {
  window.location.reload();
}

// Best-effort wipe of everything JavaScript can reach before a forced
// reload. It cannot flush the browser HTTP cache (no API exists, and
// content-hashed assets make it unnecessary), and it logs the user out
// by clearing the persisted auth state — that is the intended
// break-glass behaviour, not a side effect to avoid.
//
// localStorage/sessionStorage are cleared synchronously. CacheStorage
// and IndexedDB are wiped best-effort and deliberately NOT awaited: a
// blocked IndexedDB delete (a connection held open in another tab)
// would otherwise stall the reload forever. Neither surface is used
// today; the branches guard a future that adds one.
export function clearAllClientStorage(): void {
  try {
    localStorage.clear();
  } catch {
    // ignore
  }
  try {
    sessionStorage.clear();
  } catch {
    // ignore
  }
  try {
    if (typeof caches !== 'undefined') {
      void caches
        .keys()
        .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
        .catch(() => undefined);
    }
  } catch {
    // ignore
  }
  try {
    const idb = window.indexedDB;
    if (idb && typeof idb.databases === 'function') {
      void idb
        .databases()
        .then((databases) =>
          databases.forEach((database) => {
            if (database.name) {
              idb.deleteDatabase(database.name);
            }
          }),
        )
        .catch(() => undefined);
    }
  } catch {
    // ignore
  }
}
